import React, { useState } from 'react'
import moment from 'moment'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'
import UnavailabilityDialog from './UnavailabilityDialog'
import AddNewService, {
  serviceModalStyle,
} from './ServicingMotInsurance/AddService'
import { SectionHeader } from './SectionKit'

// A planned window whose end has passed without a service being recorded.
export const isOverduePeriod = (period) =>
  !!period.unavailabilityType &&
  !period.completedAt &&
  moment().isAfter(period.end)

// Current and upcoming planned downtime. The list itself lives in
// SingleVehicle state (fetched eagerly so the tab badge is accurate on page
// load); this section renders it and hosts the edit/delete/complete dialogs.
const UnavailabilitySection = ({
  vehicleID,
  periods,
  loading,
  error,
  onChanged,
  onAdd,
}) => {
  const [editPeriod, setEditPeriod] = useState(null)
  const [editOpen, setEditOpen] = useState(false)
  const [deletePeriod, setDeletePeriod] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(false)
  const [completePeriod, setCompletePeriod] = useState(null)

  const confirmDelete = async () => {
    if (!deletePeriod) return
    setDeleting(true)
    setDeleteError(false)
    try {
      await API.delete(`/fleet/vehicleassignment/${deletePeriod._id}`)
      setDeletePeriod(null)
      onChanged()
    } catch (e) {
      setDeleteError(true)
    } finally {
      setDeleting(false)
    }
  }

  const statusChip = (period) => {
    if (period.completedAt) return { label: 'Completed', color: 'success' }
    if (isOverduePeriod(period)) return { label: 'Overdue', color: 'error' }
    if (moment().isAfter(period.start) && moment().isBefore(period.end))
      return { label: 'Current', color: 'error' }
    return { label: 'Upcoming', color: 'default' }
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="Planned Downtime"
        caption="Scheduled services, repairs and other windows when this vehicle can't be planned onto duties or events"
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAdd}
          >
            Schedule Downtime
          </Button>
        }
      />
      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error">
          Unable to load planned downtime. Please try again later.
        </Alert>
      )}
      {!loading && !error && periods.length === 0 && (
        <Alert severity="success">
          No current or upcoming downtime recorded for this vehicle.
        </Alert>
      )}
      {!loading && periods.length > 0 && (
        <Stack spacing={1}>
          {periods.map((period) => {
            const status = statusChip(period)
            const canComplete =
              !!period.unavailabilityType && !period.completedAt
            return (
              <Stack
                key={period._id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack spacing={0.25} minWidth={0}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={700}>
                      {moment(period.start).format('DD/MM/YYYY HH:mm')} –{' '}
                      {moment(period.end).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                    <Chip
                      size="small"
                      label={period.unavailabilityType || 'Unavailable'}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                    <Chip
                      size="small"
                      label={status.label}
                      color={status.color}
                      sx={{ borderRadius: 1 }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {period.unavailabilityReason || 'No reason given'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {canComplete && (
                    <Tooltip title="Record the completed work as a service">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<TaskAltIcon />}
                        onClick={() => setCompletePeriod(period)}
                      >
                        Mark Complete
                      </Button>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditPeriod(period)
                      setEditOpen(true)
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeletePeriod(period)}
                  >
                    <DeleteOutline fontSize="small" color="error" />
                  </IconButton>
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      )}

      <UnavailabilityDialog
        open={editOpen}
        vehicleID={vehicleID}
        period={editPeriod}
        onClose={() => setEditOpen(false)}
        onSaved={onChanged}
      />

      {/* Mark complete: record the work as a service, prefilled from the
          window — saving also stamps the window as completed server-side. */}
      <BigModal
        open={!!completePeriod}
        onClose={() => setCompletePeriod(null)}
        style={serviceModalStyle}
      >
        <AddNewService
          // AddNewService reloads the vehicle itself on a successful save.
          onClose={() => {
            setCompletePeriod(null)
            onChanged()
          }}
          prefill={
            completePeriod
              ? {
                  date: completePeriod.start,
                  workCompleted: completePeriod.unavailabilityReason || '',
                }
              : undefined
          }
          plannedUnavailabilityId={completePeriod?._id}
        />
      </BigModal>

      <Dialog open={!!deletePeriod} onClose={() => setDeletePeriod(null)}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Remove this downtime window?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deletePeriod &&
              `${moment(deletePeriod.start).format('DD/MM/YYYY HH:mm')} – ${moment(
                deletePeriod.end,
              ).format('DD/MM/YYYY HH:mm')}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The vehicle will become available for planning during this window
            again.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Unable to remove this window. Please try again later.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletePeriod(null)}
            color="inherit"
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default UnavailabilitySection
